import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import type { PlaygroundPrompts } from '@api/demand-playground';
import {
  addRow,
  removeRow,
  sectionNames,
  updateRow,
  updateVersionPrompt,
} from '../utils/playground';

/** The main writer instructions, shown open; the rest are folded away. */
export const MAIN_KEY = 'system_generation';

/** Plain names for the kinds of step a section has. */
const KIND_NAMES: Record<string, string> = {
  generation: 'writing',
  extraction: 'reading',
};
const KIND_HELP: Record<string, string> = {
  generation:
    'Writing steps: step 1 writes the section, later steps polish it.',
  extraction:
    'Reading steps: pull the facts for this section out of the records.',
};
const KIND_ORDER = ['generation', 'extraction'];

const kindName = (kind: string) => KIND_NAMES[kind] ?? kind.replace(/_/g, ' ');

/** "user_generation" as "User generation". */
const keyLabel = (key: string) => {
  const text = key.replace(/_/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const kindsOf = (rows: Record<string, string[]>) => {
  const present = Object.keys(rows ?? {});
  return [
    ...KIND_ORDER.filter((k) => present.includes(k)),
    ...present.filter((k) => !KIND_ORDER.includes(k)),
  ];
};

interface Props {
  prompts: PlaygroundPrompts;
  order: string[];
  onChange: (prompts: PlaygroundPrompts) => void;
}

/**
 * The prompt editor (ZLAYT-1660): the main instructions open, the other
 * single texts folded, and per section its writing and reading steps, each
 * step editable, with add and remove.
 */
function PromptEditor({ prompts, order, onChange }: Props) {
  const otherKeys = Object.keys(prompts.version_prompts).filter(
    (key) => key !== MAIN_KEY
  );

  return (
    <Stack spacing={2}>
      {MAIN_KEY in prompts.version_prompts ? (
        <TextField
          label="Main instructions"
          helperText="What the writer is told before every section."
          multiline
          minRows={6}
          maxRows={20}
          fullWidth
          value={prompts.version_prompts[MAIN_KEY] ?? ''}
          onChange={(e) =>
            onChange(updateVersionPrompt(prompts, MAIN_KEY, e.target.value))
          }
        />
      ) : null}

      {otherKeys.length ? (
        <Accordion disableGutters variant="outlined">
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>Other instructions ({otherKeys.length})</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              {otherKeys.map((key) => (
                <TextField
                  key={key}
                  label={keyLabel(key)}
                  multiline
                  minRows={3}
                  maxRows={16}
                  fullWidth
                  value={prompts.version_prompts[key] ?? ''}
                  onChange={(e) =>
                    onChange(updateVersionPrompt(prompts, key, e.target.value))
                  }
                />
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      ) : null}

      {sectionNames(order, prompts).map((section) => {
        const rows = prompts.sections[section] ?? {};
        return (
          <Accordion key={section} disableGutters variant="outlined">
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>{section}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2.5}>
                {kindsOf(rows).map((kind) => {
                  const name = `${section} ${kindName(kind)} step`;
                  const list = rows[kind] ?? [];
                  return (
                    <Box key={kind}>
                      <Typography
                        variant="body2"
                        sx={{ color: '#475467', mb: 1 }}
                      >
                        {KIND_HELP[kind] ?? `${keyLabel(kind)} steps.`}
                      </Typography>
                      <Stack spacing={1.5}>
                        {list.length === 0 ? (
                          <Typography variant="body2" sx={{ color: '#667085' }}>
                            No steps.
                          </Typography>
                        ) : null}
                        {list.map((text, index) => (
                          <Stack
                            // Rows have no ids; the index is their identity.
                            key={index}
                            direction="row"
                            spacing={1}
                            alignItems="flex-start"
                          >
                            <TextField
                              label={`Step ${index + 1}${index === 0 ? ' (main)' : ''}`}
                              inputProps={{
                                // Includes the visible label (WCAG 2.5.3).
                                'aria-label': `${name} ${index + 1}${index === 0 ? ' (main)' : ''}`,
                              }}
                              multiline
                              minRows={2}
                              maxRows={16}
                              fullWidth
                              value={text}
                              onChange={(e) =>
                                onChange(
                                  updateRow(
                                    prompts,
                                    section,
                                    kind,
                                    index,
                                    e.target.value
                                  )
                                )
                              }
                            />
                            <Tooltip title="Remove this step">
                              <IconButton
                                aria-label={`Remove ${name} ${index + 1}${index === 0 ? ' (main)' : ''}`}
                                onClick={() =>
                                  onChange(
                                    removeRow(prompts, section, kind, index)
                                  )
                                }
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        ))}
                        <Box>
                          <Button
                            size="small"
                            aria-label={`Add ${name}`}
                            onClick={() =>
                              onChange(addRow(prompts, section, kind))
                            }
                          >
                            Add {kindName(kind)} step
                          </Button>
                        </Box>
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Stack>
  );
}

export default PromptEditor;
